import { FormEvent, useEffect, useState } from "react"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { ECourierPaymentConfirmationStatus } from "../../../Enum/ECourierPaymentConfirmationStatus"
import { useCourierPayment } from "../../../hooks/Courier/useCourierPayment"
import { CourierFinancialSummary } from "../../../Objects/DeliveryEntity/CourierFinancialSummary"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import {
  faCalendarDay,
  faClose,
  faEnvelope,
  faFileInvoiceDollar,
  faMoneyBillWave,
  faMotorcycle,
  faPhone,
} from "@fortawesome/free-solid-svg-icons"
import THEME from "../../../configuration/Theme"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { SIZE } from "../../../configuration/Sizes"
import InputField from "../../../components/InputField/InputField"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { Permissions } from "../../../Enum/PermissionsType"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { useTranslation } from "react-i18next"

interface CourierPaymentPupopProps {
  row: CourierFinancialSummary
  onClose: () => void
  onSaved: () => void
}

export const CourierPaymentPupop = ({ row, onClose, onSaved }: CourierPaymentPupopProps) => {
  const { t } = useTranslation("frontend")

  const { handleCreateCourierPayment } = useCourierPayment()

  const canEdite = !usePermission(Permissions.CanEditeCourier)

  const rejectedPayments = row.payments.filter(
    (payment) => payment.confirmationStatus === ECourierPaymentConfirmationStatus.Rejected,
  )

  const selectedRejectedPayment = rejectedPayments[0]

  const [selectedRejectedPaymentId, setSelectedRejectedPaymentId] = useState<string>(
    selectedRejectedPayment ? String(selectedRejectedPayment.id) : "",
  )

  const currentRejectedPayment = rejectedPayments.find((payment) => String(payment.id) === selectedRejectedPaymentId)

  const hasPendingRegularization = rejectedPayments.length > 0

  const isResolvingRejectedPayment = Boolean(currentRejectedPayment)

  const [amount, setAmount] = useState(String(currentRejectedPayment?.amount ?? row.outstandingAmount))

  const [paymentDate, setPaymentDate] = useState(new Date().toInputDate())

  const [notes, setNotes] = useState("")

  useEffect(() => {
    if (currentRejectedPayment) {
      setAmount(String(currentRejectedPayment.amount))
      return
    }

    setAmount(String(row.outstandingAmount))
  }, [currentRejectedPayment, row.outstandingAmount])

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const parsedAmount = Number(amount.replace(",", "."))

    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      return
    }

    const paymentDateIso = new Date(`${paymentDate}T12:00:00.000Z`).toISOString()

    await handleCreateCourierPayment(
      {
        courierId: row.courierId,
        amount: parsedAmount,
        paymentDate: paymentDateIso,
        notes,
        relatedRejectedPaymentRecordId: currentRejectedPayment?.id,
      },
      onSaved,
    )
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={isResolvingRejectedPayment ? "Regularizar contestação" : "Registrar pagamento"}
              icon={faFileInvoiceDollar}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className="flex items-center gap-2">
                  <PopupHelpButton
                    helpKey="pages.delivery.pupop.courierpaymentpupop.1"
                    variant="global"
                    title={t("helpPopup:global.deliveryCourierPayment.title")}
                    description={t("helpPopup:global.deliveryCourierPayment.description")}
                    bullets={t("helpPopup:global.deliveryCourierPayment.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.delivery.pupop.courierPaymentPupop.button.onClose"
                  />
                </div>
              }
            />

            <form onSubmit={handleSubmit} className="mt-2 space-y-4 p-1">
              <InputField
                type="text"
                animationDuration="1.2s"
                value={row.courierName}
                onChange={() => undefined}
                placeholder="Entregador"
                icon={faMotorcycle}
                disable
                descriptionKey="interactionDescriptions:pages.delivery.pupop.courierPaymentPupop.input.entregador"
              />

              <InputField
                type="text"
                animationDuration="1.3s"
                value={row.courierEmail ?? "Não informado"}
                onChange={() => undefined}
                placeholder="E-mail"
                icon={faEnvelope}
                disable
                descriptionKey="interactionDescriptions:pages.delivery.pupop.courierPaymentPupop.input.eMail"
              />

              <InputField
                type="text"
                animationDuration="1.4s"
                value={row.courierPhone ?? "Não informado"}
                onChange={() => undefined}
                placeholder="Telefone"
                icon={faPhone}
                disable
                descriptionKey="interactionDescriptions:pages.delivery.pupop.courierPaymentPupop.input.telefone"
              />

              <InputField
                required
                type="number"
                animationDuration="1.5s"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0,00"
                icon={faMoneyBillWave}
                disable={isResolvingRejectedPayment}
                descriptionKey="interactionDescriptions:pages.delivery.pupop.courierPaymentPupop.input.faMoneyBillWave"
              />

              <InputField
                required
                animationDuration="1.6s"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                placeholder="Selecione a data"
                disable={canEdite}
                icon={faCalendarDay}
                descriptionKey="interactionDescriptions:pages.delivery.pupop.courierPaymentPupop.input.selecioneAData"
              />

              {rejectedPayments.length > 0 && (
                <Animate type={AnimationTypes.slideUpIn} duration="1.7s">
                  <div className="space-y-1">
                    <label className="ml-2 text-sm font-medium text-gray-600">Contestação a regularizar</label>

                    <select
                      value={selectedRejectedPaymentId}
                      onChange={(e) => setSelectedRejectedPaymentId(e.target.value)}
                      className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-800 outline-none ring-2 ring-gray-200 transition-all duration-200 focus:border-orange-400 focus:ring-orange-500"
                      data-interaction-description-key="interactionDescriptions:pages.delivery.pupop.courierPaymentPupop.select.select"
                    >
                      {rejectedPayments.map((payment) => (
                        <option key={payment.id} value={payment.id}>
                          {`${payment.amount.toMoney()} • ${(payment.confirmationDatetime ?? payment.paymentDate).toString().toReadableDate()}`}
                        </option>
                      ))}

                      {!hasPendingRegularization && (
                        <option value="">Novo pagamento sem vínculo com contestação</option>
                      )}
                    </select>

                    <p className="ml-2 text-xs text-slate-500">
                      {hasPendingRegularization
                        ? "Enquanto existir contestação pendente, o sistema só permite lançar a regularização correspondente."
                        : "Selecione a contestação específica para encerrá-la no painel principal."}
                    </p>
                  </div>
                </Animate>
              )}

              <Animate type={AnimationTypes.slideUpIn} duration="1.8s">
                <div className="space-y-1 relative rounded-xl border border-gray-300 bg-white ring-2 ring-gray-200 transition-all duration-200 focus-within:border-orange-400 focus-within:ring-orange-500">
                  <div className="pointer-events-none absolute left-3 top-3 text-gray-800">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  </div>

                  <textarea
                    value={notes}
                    disabled={canEdite}
                    onChange={(e) => setNotes(e.target.value)}
                    className="min-h-28 w-full rounded-xl bg-transparent py-3 pl-10 pr-3 text-gray-800 outline-none placeholder:text-gray-400"
                    placeholder="Motivo ou observação do pagamento"
                    data-interaction-description-key="interactionDescriptions:pages.delivery.pupop.courierPaymentPupop.input.motivoOuObservacaoDoPagamento"
                  />
                </div>
              </Animate>

              <Animate type={AnimationTypes.slideUpIn} duration="1.9s">
                <div className="rounded-md border border-orange-200 bg-orange-50 px-3 py-3 text-xs text-slate-700">
                  <p className="font-bold text-red-700">Resumo do lançamento</p>

                  <p className="mt-1">
                    {isResolvingRejectedPayment
                      ? `Você está regularizando a contestação de ${(currentRejectedPayment?.amount ?? 0).toMoney()}. Após o novo lançamento, essa contestação sairá do painel principal e o novo pagamento ficará aguardando conferência do entregador.`
                      : `Saldo atual do entregador: ${row.outstandingAmount.toMoney()}. Após o lançamento, o pagamento ficará disponível para conferência do entregador.`}
                  </p>

                  {hasPendingRegularization && (
                    <p className="mt-2 font-medium text-red-700">
                      Novos pagamentos ficam bloqueados até a regularização da contestação pendente.
                    </p>
                  )}
                </div>
              </Animate>

              <div className={defaultStyle.divisionBar}></div>

              <Button
                typeButton="submit"
                type="primary"
                size="medium"
                label={isResolvingRejectedPayment ? "Salvar regularização" : "Confirmar lançamento"}
                bgColor={THEME.colors.buttonBGSaveColor}
                disabled={canEdite}
                descriptionKey="interactionDescriptions:pages.delivery.pupop.courierPaymentPupop.button.isResolvingRejectedPaymentSalvarRegularizacaoConfirmarLancam"
              />
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
